import React from "react";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import PageContentContainer from "../../Container/PageContentContainer";
import { ProductHeadline } from "../../PageHeadline/PageHeadline";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import { useCase } from "../../../provider/CaseProvider";
import { fetchResource, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { getCaseLink } from "../../../services/Product/ProductService/productService";
import ExecutionJobDetailsCard from "./ExecutionJobDetailsCard";
import ExecutionJobStepsTable from "./ExecutionJobStepsTable";
import { ExecutionJob } from "./caseExecutionJobDetailsTypes";
import { isExecutionJobDifferentCase } from "./caseExecutionJobDetailsUtils";
import { useExecutionJobActions } from "./useExecutionJobActions";

const CaseExecutionJobDetailsPage = () => {
  const { executionJobId } = useParams();
  const { product } = useCase();
  const navigate = useNavigate();
  const executionJobIdAsNumber = parseInt(executionJobId || "-1");
  const [openStepIds, setOpenStepIds] = React.useState<number[]>([]);

  const { data: executionJob, isLoading } = useQuery({
    queryKey: queryKeys.item("execution_jobs", executionJobIdAsNumber),
    queryFn: () => fetchResource("execution_jobs", executionJobIdAsNumber) as Promise<ExecutionJob>
  });

  const isExecutionAllowed = executionJob?.status === "pendingApproval";
  const isJobPendingApproval = executionJob?.status === "pendingApproval";
  const isRejectionAllowed = executionJob?.status === "pendingApproval";
  const {
    canReorderSteps,
    executeJob,
    isExecuting,
    isRejecting,
    orderedSteps,
    rejectJob,
    reorderExecutionSteps,
    stepIdsWithPendingStatusToggle,
    toggleStepSkipped,
  } = useExecutionJobActions({
    executionJob,
    caseIri: product?.backofficeCase?.["@id"],
    isExecutionAllowed,
    isRejectionAllowed,
  });

  if (!executionJobId) {
    return null;
  }

  if (!product || isLoading || !executionJob) {
    return <LegalBirdPageLoading />;
  }

  if (isExecutionJobDifferentCase(executionJob, product.backofficeCase)) {
    return (
      <PageContentContainer size={"small"}>
        <Typography variant={"h3"}>Dieser Execution Job gehört nicht zu diesem Fall.</Typography>
      </PageContentContainer>
    );
  }

  const toggleStepOpen = (stepId: number) => {
    setOpenStepIds((previousOpenStepIds) =>
      previousOpenStepIds.includes(stepId)
        ? previousOpenStepIds.filter((id) => id !== stepId)
        : [...previousOpenStepIds, stepId]
    );
  };

  return (
    <PageContentContainer>
      <ProductHeadline title={`Execution Job #${executionJob.id} zu`} product={product} />

      <ExecutionJobDetailsCard
        executionJob={executionJob}
        isExecuting={isExecuting}
        isRejecting={isRejecting}
        isExecutionAllowed={isExecutionAllowed}
        isRejectionAllowed={isRejectionAllowed}
        onBack={() => navigate(getCaseLink(product) + "/execution-jobs")}
        onExecute={executeJob}
        onReject={rejectJob}
      />

      <ExecutionJobStepsTable
        executionJobId={executionJob.id}
        steps={orderedSteps}
        openStepIds={openStepIds}
        canReorderSteps={canReorderSteps}
        isJobPendingApproval={isJobPendingApproval}
        onReorder={reorderExecutionSteps}
        onToggleStepOpen={toggleStepOpen}
        onToggleStepSkipped={toggleStepSkipped}
        stepIdsWithPendingStatusToggle={stepIdsWithPendingStatusToggle}
      />
    </PageContentContainer>
  );
};

export default CaseExecutionJobDetailsPage;
